import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("pr_token")}` });
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => iso(new Date());
const fyEnd = () => { const n = new Date(); const y = n.getMonth() >= 3 ? n.getFullYear() + 1 : n.getFullYear(); return `${y}-03-31`; };
const keyOf = (r) => `${r.supplier_code}|${r.material_code}|${r.valid_to}`;
const nextFrom = (vt) => { const [y, m, dd] = vt.split("-").map(Number); const d = new Date(y, m - 1, dd + 1); const s = iso(d); return s < todayIso() ? todayIso() : s; };
const blockedWhy = (r) => (r.renewed_to ? `Renewed to ${r.renewed_to}` : r.pending_draft ? `Draft ${r.pending_draft} pending` : r.has_scales ? "Has qty scales — renew manually" : null);

function RenewRow({ r, sel, rate, onToggle, onRate }) {
  const why = blockedWhy(r);
  return (
    <tr className={`border-t border-slate-100 ${why ? "opacity-60" : ""}`} data-testid={`exp-row-${r.supplier_code}-${r.material_code}-${r.valid_to}`}>
      <td className="px-2 py-1.5"><input type="checkbox" disabled={!!why} checked={sel} onChange={onToggle} data-testid={`exp-check-${r.supplier_code}-${r.material_code}-${r.valid_to}`} /></td>
      <td className="px-2 py-1.5"><div className="font-mono font-semibold">{r.material_code}</div><div className="text-slate-500 truncate max-w-[220px]">{r.material_desc}</div></td>
      <td className="px-2 py-1.5"><div className="font-mono">{r.supplier_code}</div><div className="text-slate-500 truncate max-w-[200px]">{r.supplier_name}</div></td>
      <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{r.amount} {r.currency_code}<div className="text-[10px] text-slate-400">per {r.base_quantity} {r.unit_code}</div></td>
      <td className="px-2 py-1.5 whitespace-nowrap">{r.valid_to}<div className={`text-[10px] ${r.days_left <= 3 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{r.days_left} day(s) left</div></td>
      <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{why ? <span className="text-amber-700">{why}</span> : `from ${nextFrom(r.valid_to)}`}</td>
      <td className="px-2 py-1.5 w-28">
        <Input type="number" step="0.01" min="0" onWheel={(ev) => ev.currentTarget.blur()} onKeyDown={(ev) => { if (ev.key === "ArrowUp" || ev.key === "ArrowDown") ev.preventDefault(); }} value={rate} disabled={!!why} placeholder={String(r.amount)}
          onChange={(e) => onRate(e.target.value)} className="h-7 text-xs placeholder:text-slate-300" data-testid={`exp-rate-${r.supplier_code}-${r.material_code}-${r.valid_to}`} />
      </td>
    </tr>
  );
}

function Toolbar({ days, setDays, q, setQ, reload, loading, count, truncated }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Clock size={16} className="text-cyan-700" />
      <div className="text-sm font-semibold text-slate-800">List prices expiring in</div>
      <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded-sm text-xs px-2 py-1" data-testid="exp-days-select">
        {[7, 15, 30, 60].map((d) => <option key={d} value={d}>next {d} days</option>)}
      </select>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter supplier / item" className="h-8 text-xs w-52" data-testid="exp-search-input" />
      <Button size="sm" variant="ghost" onClick={reload} disabled={loading} data-testid="exp-reload-btn">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
      </Button>
      <span className="text-xs text-slate-500" data-testid="exp-count">{count} price(s){truncated ? " — showing first 2000, narrow the filter" : ""}</span>
    </div>
  );
}

export function ExpiringRenewPanel({ onDone }) {
  const [days, setDays] = useState(15);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [rates, setRates] = useState({});
  const [validTo, setValidTo] = useState(fyEnd());
  const [direct, setDirect] = useState(false);
  const [busy, setBusy] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/list-prices/expiring`, { params: { days, q }, headers: headers() });
      setRows(data.rows || []); setSel({}); setTruncated(!!data.truncated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load expiring prices");
    } finally { setLoading(false); }
  }, [days, q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const selectable = useMemo(() => rows.filter((r) => !blockedWhy(r)), [rows]);
  const chosen = selectable.filter((r) => sel[keyOf(r)]);
  const allOn = selectable.length > 0 && chosen.length === selectable.length;
  const toggleAll = () => setSel(allOn ? {} : Object.fromEntries(selectable.map((r) => [keyOf(r), true])));

  const submit = async () => {
    const badRate = chosen.find((r) => { const v = rates[keyOf(r)]; return v !== undefined && v !== "" && Math.abs(Math.round(Number(v) * 100) - Number(v) * 100) > 1e-6; });
    if (badRate) return toast.error(`${badRate.material_code}: max 2 decimals (use Base Qty for smaller rates)`);
    const bad = chosen.find((r) => validTo <= nextFrom(r.valid_to));
    if (bad) return toast.error(`New end date must be after ${nextFrom(bad.valid_to)} (${bad.material_code})`);
    if (direct && !window.confirm(`Write ${chosen.length} renewed price(s) straight to SAP without approval?`)) return;
    setBusy(true);
    try {
      const payload = chosen.map((r) => {
        const nr = rates[keyOf(r)];
        const amount = nr !== undefined && nr !== "" ? Number(nr) : r.amount;
        return { supplier_code: r.supplier_code, material_code: r.material_code, amount, currency_code: r.currency_code,
          base_quantity: r.base_quantity, unit_code: r.unit_code, valid_from: nextFrom(r.valid_to), valid_to: validTo,
          notes: `Bulk renew of ${r.amount} ${r.currency_code} (ended ${r.valid_to})${amount !== r.amount ? ` → ${amount}` : ""}` };
      });
      const { data } = await axios.post(`${API}/admin/list-prices/direct-import`,
        { rows: payload, note: `Bulk renew to ${validTo}`, direct, exempt_non_bom: false, renewal: true }, { headers: headers(), timeout: 300000 });
      toast.success(`${data.created.length} renewal(s) ${direct ? "sent to SAP" : "sent for L1 → L2 approval"}`);
      if (data.failed.length) toast.error(`${data.failed.length} failed: ${data.failed.slice(0, 3).map((f) => `${f.material_code}: ${f.error}`).join(" · ")}`, { duration: 12000 });
      setRates({}); load(); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renew failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="exp-panel">
      <Toolbar {...{ days, setDays, q, setQ, reload: load, loading, count: rows.length, truncated }} />
      <div className="max-h-[55vh] overflow-auto border border-slate-200 rounded-sm">
        <table className="w-full text-xs" data-testid="exp-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 text-left sticky top-0">
            <tr>
              <th className="px-2 py-1.5"><input type="checkbox" checked={allOn} onChange={toggleAll} data-testid="exp-check-all" /></th>
              <th className="px-2 py-1.5">Item</th><th className="px-2 py-1.5">Supplier</th><th className="px-2 py-1.5 text-right">Current rate</th>
              <th className="px-2 py-1.5">Ends</th><th className="px-2 py-1.5">Renewal</th><th className="px-2 py-1.5">New rate (optional)</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="exp-empty">No list prices expire in this window.</td></tr>}
            {rows.map((r) => (
              <RenewRow key={keyOf(r)} r={r} sel={!!sel[keyOf(r)]} rate={rates[keyOf(r)] ?? ""}
                onToggle={() => setSel((p) => ({ ...p, [keyOf(r)]: !p[keyOf(r)] }))}
                onRate={(v) => setRates((p) => ({ ...p, [keyOf(r)]: v }))} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-2">New end date
          <Input type="date" value={validTo} min={todayIso()} onChange={(e) => setValidTo(e.target.value)} className="h-8 text-xs w-40" data-testid="exp-valid-to" />
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={direct} onChange={(e) => setDirect(e.target.checked)} data-testid="exp-direct-toggle" />
          Write straight to SAP — <b>skip L1/L2 approval</b>
        </label>
        <div className="flex-1" />
        <span className="text-slate-500">Blank rate = same rate. Each renewal starts the day after the current one ends.</span>
        <Button size="sm" onClick={submit} disabled={busy || !chosen.length || !validTo} className="bg-cyan-700 hover:bg-cyan-800" data-testid="exp-renew-btn">
          {busy && <Loader2 size={14} className="animate-spin mr-1" />}
          {direct ? `Renew ${chosen.length} in SAP` : `Renew ${chosen.length} — send for approval`}
        </Button>
      </div>
    </div>
  );
}
